/**
 * Audit Log Component
 *
 * Displays audit logs with filtering, pagination, and search capabilities.
 * Shows user actions, resource changes, and security events.
 */

import { useState, useEffect, useCallback } from 'react';
import { Layout } from '../layout/Layout';
import { useAuth } from '../../contexts/AuthContext';
import { getApiClient, isSuccess } from '../../api/client';

// =============================================================================
// Types
// =============================================================================

interface AuditLogEntry {
  id: string;
  userId: string;
  action: string;
  resource: string;
  resourceId: string | null;
  details: Record<string, unknown> | null;
  ip: string | null;
  userAgent: string | null;
  createdAt: string;
  user: {
    id: string;
    name: string;
    email: string;
    role: string;
    tenantId: string | null;
  };
}

interface AuditLogsResponse {
  logs: AuditLogEntry[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasMore: boolean;
  };
}

interface AuditSummary {
  totalActions: number;
  actionBreakdown: Record<string, number>;
  resourceBreakdown: Record<string, number>;
  topUsers: Array<{ userId: string; name: string; count: number }>;
}

// =============================================================================
// Component
// =============================================================================

export function AuditLog() {
  useAuth(); // Ensure user is authenticated
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [summary, setSummary] = useState<AuditSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });

  // Filters
  const [actionFilter, setActionFilter] = useState<string>('');
  const [resourceFilter, setResourceFilter] = useState<string>('');
  const [dateRange, setDateRange] = useState<{ start: string; end: string }>({
    start: '',
    end: '',
  });

  const fetchLogs = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      const apiClient = getApiClient();

      const params = new URLSearchParams({
        page: page.toString(),
        limit: pagination.limit.toString(),
        sortBy: 'createdAt',
        sortOrder: 'desc',
      });

      if (actionFilter) params.append('action', actionFilter);
      if (resourceFilter) params.append('resource', resourceFilter);
      if (dateRange.start) params.append('startDate', new Date(dateRange.start).toISOString());
      if (dateRange.end) params.append('endDate', new Date(dateRange.end).toISOString());

      const response = await apiClient.get<AuditLogsResponse>(
        `/reports/audit?${params.toString()}`
      );

      if (!isSuccess(response)) {
        throw new Error('Failed to fetch audit logs');
      }

      const data = response.data;
      setLogs(data.logs);
      setPagination({
        page: data.pagination.page,
        limit: data.pagination.limit,
        total: data.pagination.total,
        totalPages: data.pagination.totalPages,
      });
      setError(null);
    } catch (err) {
      setError('Failed to load audit logs');
      console.error('Audit log fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [pagination.limit, actionFilter, resourceFilter, dateRange]);

  const fetchSummary = useCallback(async () => {
    try {
      const apiClient = getApiClient();

      const params = new URLSearchParams();
      if (dateRange.start) params.append('startDate', new Date(dateRange.start).toISOString());
      if (dateRange.end) params.append('endDate', new Date(dateRange.end).toISOString());

      const response = await apiClient.get<AuditSummary>(
        `/reports/audit/summary?${params.toString()}`
      );

      if (isSuccess(response)) {
        setSummary(response.data);
      }
    } catch (err) {
      console.error('Audit summary fetch error:', err);
    }
  }, [dateRange]);

  useEffect(() => {
    fetchLogs(1);
    fetchSummary();
  }, [fetchLogs, fetchSummary]);

  const getActionColor = (action: string) => {
    if (action.includes('CREATE') || action.includes('create')) return 'text-green-400';
    if (action.includes('DELETE') || action.includes('delete')) return 'text-red-400';
    if (action.includes('UPDATE') || action.includes('update')) return 'text-yellow-400';
    if (action.includes('LOGIN') || action.includes('login')) return 'text-blue-400';
    return 'text-gray-400';
  };

  const formatDetails = (details: Record<string, unknown> | null) => {
    if (!details) return '-';
    const entries = Object.entries(details).slice(0, 3);
    return entries.map(([key, value]) => `${key}: ${JSON.stringify(value)}`).join(', ');
  };

  if (loading && logs.length === 0) {
    return (
      <Layout>
        <div className="flex h-64 items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-discord-blurple border-t-transparent" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-white">Audit Log</h1>
          <p className="mt-1 text-sm text-gray-400">
            Track user actions and system events
          </p>
        </div>

        {error && (
          <div className="rounded-md bg-red-500/10 border border-red-500/20 p-4 text-red-400">
            {error}
          </div>
        )}

        {/* Summary Cards */}
        {summary && (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-lg bg-discord-dark p-4">
              <div className="text-sm text-gray-400">Total Actions</div>
              <div className="mt-1 text-2xl font-bold text-white">{summary.totalActions}</div>
            </div>
            <div className="rounded-lg bg-discord-dark p-4">
              <div className="text-sm text-gray-400">Action Types</div>
              <div className="mt-1 text-2xl font-bold text-white">
                {Object.keys(summary.actionBreakdown).length}
              </div>
            </div>
            <div className="rounded-lg bg-discord-dark p-4">
              <div className="text-sm text-gray-400">Resources Modified</div>
              <div className="mt-1 text-2xl font-bold text-white">
                {Object.keys(summary.resourceBreakdown).length}
              </div>
            </div>
            <div className="rounded-lg bg-discord-dark p-4">
              <div className="text-sm text-gray-400">Active Users</div>
              <div className="mt-1 text-2xl font-bold text-white">
                {summary.topUsers.length}
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="rounded-lg bg-discord-dark p-4">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <label className="block text-sm font-medium text-gray-400">Action</label>
              <input
                type="text"
                value={actionFilter}
                onChange={(e) => setActionFilter(e.target.value)}
                placeholder="e.g., CREATE, DELETE"
                className="mt-1 w-full rounded-md border border-discord-light bg-discord-darker px-3 py-2 text-sm text-white placeholder-gray-500 focus:border-discord-blurple focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-400">Resource</label>
              <input
                type="text"
                value={resourceFilter}
                onChange={(e) => setResourceFilter(e.target.value)}
                placeholder="e.g., bot, user"
                className="mt-1 w-full rounded-md border border-discord-light bg-discord-darker px-3 py-2 text-sm text-white placeholder-gray-500 focus:border-discord-blurple focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-400">Start Date</label>
              <input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange((prev) => ({ ...prev, start: e.target.value }))}
                className="mt-1 w-full rounded-md border border-discord-light bg-discord-darker px-3 py-2 text-sm text-white focus:border-discord-blurple focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-400">End Date</label>
              <input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange((prev) => ({ ...prev, end: e.target.value }))}
                className="mt-1 w-full rounded-md border border-discord-light bg-discord-darker px-3 py-2 text-sm text-white focus:border-discord-blurple focus:outline-none"
              />
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <button
              onClick={() => fetchLogs(1)}
              className="rounded-md bg-discord-blurple px-4 py-2 text-sm font-medium text-white hover:bg-discord-blurple/80"
            >
              Apply Filters
            </button>
          </div>
        </div>

        {/* Logs Table */}
        <div className="rounded-lg bg-discord-dark p-6">
          <h2 className="mb-4 text-lg font-semibold text-white">Recent Activity</h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-discord-light text-left text-sm text-gray-400">
                  <th className="pb-3 pr-4">Timestamp</th>
                  <th className="pb-3 pr-4">User</th>
                  <th className="pb-3 pr-4">Action</th>
                  <th className="pb-3 pr-4">Resource</th>
                  <th className="pb-3 pr-4">Details</th>
                  <th className="pb-3">IP</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                {logs.map((log) => (
                  <tr key={log.id} className="border-b border-discord-light/50">
                    <td className="py-3 pr-4 text-gray-300">
                      {new Date(log.createdAt).toLocaleString()}
                    </td>
                    <td className="py-3 pr-4">
                      <div className="text-white">{log.user.name}</div>
                      <div className="text-xs text-gray-500">{log.user.email}</div>
                    </td>
                    <td className={`py-3 pr-4 font-medium ${getActionColor(log.action)}`}>
                      {log.action}
                    </td>
                    <td className="py-3 pr-4">
                      <span className="text-white">{log.resource}</span>
                      {log.resourceId && (
                        <span className="ml-1 text-xs text-gray-500">
                          ({log.resourceId.slice(0, 8)}...)
                        </span>
                      )}
                    </td>
                    <td className="py-3 pr-4 text-gray-400 max-w-xs truncate">
                      {formatDetails(log.details)}
                    </td>
                    <td className="py-3 text-gray-400">{log.ip || '-'}</td>
                  </tr>
                ))}
                {logs.length === 0 && (
                  <tr>
                    <td colSpan={6} className="py-8 text-center text-gray-400">
                      No audit logs found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between">
              <div className="text-sm text-gray-400">
                Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
                {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => fetchLogs(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  className="rounded-md bg-discord-light px-3 py-1 text-sm text-white disabled:opacity-50 hover:bg-discord-light/80"
                >
                  Previous
                </button>
                <span className="px-3 py-1 text-sm text-gray-400">
                  Page {pagination.page} of {pagination.totalPages}
                </span>
                <button
                  onClick={() => fetchLogs(pagination.page + 1)}
                  disabled={pagination.page === pagination.totalPages}
                  className="rounded-md bg-discord-light px-3 py-1 text-sm text-white disabled:opacity-50 hover:bg-discord-light/80"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}

export default AuditLog;
